import { I18nPluralPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, InjectionToken, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { fad } from '@fortawesome/pro-duotone-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { ActionReducerMap, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StorageModule } from '@ngx-pwa/local-storage';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { RequireConfirmedContactMethodsGuard } from '@services/require-confirmed-contact-methods-guard.service';
import { SupportFaqComponent } from '@training-manager/components/support-faq/support-faq.component';
import { DefaultMissingTranslationHandler, LazyTranslateLoader } from '@util/i18n';
import { Angulartics2Module } from 'angulartics2';
import { MarkdownModule } from 'ngx-markdown';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

import { AuthTokenService } from '@services/auth-token.service';
import { RequireAcceptedTermsOfServicesGuard } from '@services/require-accepted-terms-of-services-guard.service';
import { VendorRoleGuard } from '@services/vendor-role-guard.service';
import { SurveysModule } from '@surveys/surveys.module';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { ConfirmContactComponent } from './components/confirm-contact/confirm-contact.component';
import { ContactNotificationComponent } from './components/contact-notification/contact-notification.component';
import { FieldsRequiredNotificationComponent } from './components/fields-required-notification/fields-required-notification.component';
import { ImpersonationNotificationComponent } from './components/impersonation-notification/impersonation-notification.component';
import { InviteNotificationComponent } from './components/invite-notification/invite-notification.component';
import { NotificationBarComponent } from './components/notification-bar/notification-bar.component';
import { PersistentNotificationComponent } from './components/persistent-notification/persistent-notification.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { StorefrontBrandComponent } from './components/storefront-brand/storefront-brand.component';
import { TopbarComponent } from './components/topbar/topbar.component';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { GraphQLModule } from './graphql.module';
import { IndicatorInterceptor } from './interceptors/indicator.interceptor';
import { metaReducers, reducers, State } from './reducers';
import { CanDeactivateGuard } from './services/can-deactivate-guard.service';
import { IndicatorService } from './services/indicator.service';
import { NewrelicErrorHandler } from './services/newrelic-error-handler.service';
import { SharedModule } from './shared/shared.module';
import { ThemeModule } from './theme/theme.module';
import { LVTheme, RapidTheme } from './themes';
import { WktTheme } from './themes/wkt-theme';

export const REDUCERS_TOKEN = new InjectionToken<ActionReducerMap<State>>('App Reducers');

export const WINDOW = new InjectionToken('Window');
export function windowProvider() {
  return window;
}

export const NAVIGATOR = new InjectionToken('Navigator');
export function navigatorProvider() {
  return navigator;
}

function authTokenRunner(authTokenService: AuthTokenService) {
  return authTokenService.authenticateWithToken();
}

@NgModule({
  declarations: [
    AppComponent,
    BreadcrumbComponent,
    ConfirmContactComponent,
    ConfirmContactComponent,
    ContactNotificationComponent,
    FieldsRequiredNotificationComponent,
    ImpersonationNotificationComponent,
    InviteNotificationComponent,
    NotificationBarComponent,
    PersistentNotificationComponent,
    SidebarComponent,
    StorefrontBrandComponent,
    SupportFaqComponent,
    TopbarComponent,
    UserMenuComponent
  ],
  imports: [
    BrowserModule,
    FontAwesomeModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    Angulartics2Module.forRoot(),

    TranslateModule.forRoot({
      defaultLanguage: 'en',
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: DefaultMissingTranslationHandler
      },
      loader: {
        provide: TranslateLoader,
        useClass: LazyTranslateLoader
      }
    }),
    GraphQLModule,
    SurveysModule,
    ThemeModule.forRoot({
      // load these or fetch from server..
      themes: [RapidTheme, LVTheme, WktTheme],
      active: RapidTheme
    }),
    SharedModule,
    MarkdownModule.forRoot(),
    StoreModule.forRoot(REDUCERS_TOKEN, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    StorageModule.forRoot({
      LSPrefix: 'venom_',
      IDBDBName: 'venom_ngStorage'
    }),
    !environment.production ? StoreDevtoolsModule.instrument() : []
  ],
  providers: [
    { provide: APP_INITIALIZER, useFactory: authTokenRunner, deps: [AuthTokenService], multi: true },
    { provide: ErrorHandler, useClass: NewrelicErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: IndicatorInterceptor, multi: true },
    { provide: REDUCERS_TOKEN, useValue: reducers },
    { provide: WINDOW, useFactory: windowProvider },
    { provide: NAVIGATOR, useFactory: navigatorProvider },
    CanDeactivateGuard,
    ConfirmationService,
    DialogService,
    I18nPluralPipe,
    IndicatorService,
    MessageService,
    RequireConfirmedContactMethodsGuard,
    RequireAcceptedTermsOfServicesGuard,
    VendorRoleGuard
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(faLibrary: FaIconLibrary) {
    faLibrary.addIconPacks(fas, fad);
  }
}
